import { StandardEvents } from '@shopify/events';
import { publish, readAnalyticsItem, centsToDecimal, normalizeId } from '@theme/analytics';

/**
 * Builds an Item payload from a Shopify Ajax Cart API line (`/cart.js`, `/cart/add.js`).
 * @param {Record<string, any>} line
 * @param {number} quantity
 * @returns {Record<string, unknown>}
 */
function buildItemFromAjaxLine(line, quantity) {
  const price = centsToDecimal(line.price);
  const discount = Math.max(0, centsToDecimal(line.original_price) - price);

  return {
    item_id: String(line.product_id),
    item_variant: String(line.variant_id),
    item_name: line.product_title || line.title || '',
    price,
    discount,
    item_brand: line.vendor || '',
    item_category: line.product_type || '',
    quantity,
    unavailable: false,
  };
}

function sumValue(items) {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

// Every publish below is chained onto this instead of firing as soon as its own async work
// resolves. cartLinesUpdate/cartView handlers run synchronously in dispatch order, so chaining
// here (rather than inside the async .then) preserves that order for the actual publish() calls
// even when a later-dispatched event's async work (e.g. view_cart's own /cart.js fetch) happens
// to resolve faster than an earlier-dispatched one's (e.g. add_to_cart waiting on product-form's
// /cart/add.js + follow-up /cart.js round trip) — otherwise view_cart could publish before the
// add_to_cart that opened the drawer in the first place.
let publishQueue = Promise.resolve();

// product-form.js dispatches this synchronously (action: 'add') before the /cart/add.js
// fetch; the promise resolves with the full post-add cart from a follow-up /cart.js fetch.
// Publishes one add_to_cart per product added, each with a single-item array — even when
// a single action adds multiple lines at once (e.g. a kit/bundle "add all" button), since
// the event is meant to represent one product addition, not a batch.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;

  publishQueue = publishQueue.then(() =>
    event.promise
      ?.then(({ detail }) => {
        if (!detail || detail.didError) return;

        const items = event.lines
          .map((line) => {
            const merchandiseId = normalizeId(line.merchandiseId);
            const ajaxLine = detail.items?.find((item) => String(item.variant_id) === merchandiseId);
            return ajaxLine ? buildItemFromAjaxLine(ajaxLine, line.quantity) : null;
          })
          .filter(Boolean);

        for (const item of items) {
          publish('add_to_cart', { currency: 'BRL', value: item.price * item.quantity, items: [item] });
        }
      })
      .catch(() => {})
  );
});

// component-cart-items.js dispatches this synchronously (action: 'update'/'remove') before
// the /cart/change.js fetch — read the pre-mutation quantity now, decide add vs. remove from
// the delta (the quantity selector can also increase an existing line), and only publish once
// the request actually succeeds.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'update' && event.action !== 'remove') return;

  const [line] = event.lines;
  if (!line) return;

  const row = document.querySelector(`[data-key="${CSS.escape(String(line.id))}"]`);
  const item = readAnalyticsItem(row);
  if (!item) return;

  const previousQuantity = parseInt(row?.querySelector('input[data-cart-quantity]')?.getAttribute('data-cart-quantity') ?? '', 10) || 0;
  const delta = line.quantity - previousQuantity;
  if (delta === 0) return;

  publishQueue = publishQueue.then(() =>
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        if (delta > 0) {
          publish('add_to_cart', { currency: 'BRL', value: item.price * delta, items: [{ ...item, quantity: delta }] });
        } else {
          publish('remove_from_cart', { currency: 'BRL', value: item.price * -delta, items: [{ ...item, quantity: -delta }] });
        }
      })
      .catch(() => {})
  );
});

// cart-items-component dispatches shopify:cart:view (view-event-trigger="dialog") both on the
// cart page and when the drawer opens. Its native payload is too shallow (no vendor/category/
// compare_at_price), so fetch the full cart here instead of reading it.
document.addEventListener(StandardEvents.cartView, () => {
  publishQueue = publishQueue.then(() =>
    fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const items = (cart.items || []).map((line) => buildItemFromAjaxLine(line, line.quantity));
        publish('view_cart', { currency: 'BRL', value: sumValue(items), items });
      })
      .catch(() => {})
  );
});
